import React from 'react';
import { StorageBin, LocationData } from '../../types/inventory';
import { MapPin, Package, BarChart3, Loader2 } from 'lucide-react';

interface LocationOverviewProps {
  bins: StorageBin[];
  racks: LocationData | null;
  loading: boolean;
}

const LocationOverview: React.FC<LocationOverviewProps> = ({ bins, racks, loading }) => {
  const calculateUtilization = (current: number, capacity: number) => {
    return capacity > 0 ? Math.round((current / capacity) * 100) : 0;
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization >= 90) return 'text-red-600 bg-red-100';
    if (utilization >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="text-gray-600 mt-2">Loading location data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Location Overview</h2>
        <p className="text-gray-600">Monitor storage bin and rack utilization</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold text-blue-900">{bins.length}</p>
              <p className="text-blue-700">Total Bins</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center">
            <MapPin className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold text-green-900">
                {(racks?.regular_racks?.length || 0) + (racks?.jean_racks?.length || 0)}
              </p>
              <p className="text-green-700">Total Racks</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold text-yellow-900">
                {bins.length > 0 
                  ? Math.round(bins.reduce((sum, bin) => sum + calculateUtilization(bin.current_stock, bin.capacity), 0) / bins.length)
                  : 0}%
              </p>
              <p className="text-yellow-700">Avg Bin Utilization</p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 p-4 rounded-lg">
          <div className="flex items-center">
            <Package className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-2xl font-semibold text-purple-900">
                {bins.reduce((sum, bin) => sum + bin.current_stock, 0)}
              </p>
              <p className="text-purple-700">Total Stock (Bins)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Storage Bins */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Storage Bins</h3>
        
        {bins.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No bins data available
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Bin Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current Stock
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Capacity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Utilization
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Styles
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bins.slice(0, 20).map((bin) => {
                    const utilization = calculateUtilization(bin.current_stock, bin.capacity);
                    return (
                      <tr key={bin.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {bin.bin_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {bin.current_stock}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {bin.capacity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getUtilizationColor(utilization)}`}>
                            {utilization}%
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex flex-wrap gap-1">
                            {bin.styles.slice(0, 3).map((style, index) => (
                              <span key={index} className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                {style}
                              </span>
                            ))}
                            {bin.styles.length > 3 && (
                              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                +{bin.styles.length - 3}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {bins.length > 20 && (
              <div className="bg-gray-50 px-6 py-3 text-sm text-gray-600 text-center">
                Showing first 20 bins of {bins.length} total
              </div>
            )}
          </div>
        )}
      </div>

      {/* Storage Racks */}
      {racks && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Storage Racks</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Regular Racks */}
            <div>
              <h4 className="text-md font-medium text-gray-800 mb-3">Regular Racks</h4>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                {racks.regular_racks.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No regular racks data</div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {racks.regular_racks.map((rack) => (
                      <div key={rack.id} className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <h5 className="font-medium text-gray-900">{rack.rack_number}</h5>
                          <span className="text-sm text-gray-600">
                            {rack.current_items || 0} items
                          </span>
                        </div>
                        
                        {rack.styles.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {rack.styles.slice(0, 4).map((style, index) => (
                              <span key={index} className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                {style}
                              </span>
                            ))}
                            {rack.styles.length > 4 && (
                              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                +{rack.styles.length - 4}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Jean Racks */}
            <div>
              <h4 className="text-md font-medium text-gray-800 mb-3">Jean Racks</h4>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                {racks.jean_racks.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">No jean racks data</div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {racks.jean_racks.map((rack) => {
                      const utilization = calculateUtilization(rack.current_stock, rack.capacity);
                      return (
                        <div key={rack.id} className="p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h5 className="font-medium text-gray-900">{rack.rack_number}</h5>
                              {rack.gender && (
                                <span className="text-xs text-gray-500 capitalize">{rack.gender} jeans</span>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-sm text-gray-900">{rack.current_stock}/{rack.capacity}</span>
                              <span className={`block text-xs px-2 py-1 rounded ${getUtilizationColor(utilization)}`}>
                                {utilization}%
                              </span>
                            </div>
                          </div>
                          
                          {rack.styles.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {rack.styles.slice(0, 4).map((style, index) => (
                                <span key={index} className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                                  {style}
                                </span>
                              ))}
                              {rack.styles.length > 4 && (
                                <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                  +{rack.styles.length - 4}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationOverview;
